import { useTranslation } from "react-i18next";
import { stageBranchesErrorDetail, stageBranchesErrorText } from "../stageBranchesActive";
import { Alert, AlertTitle } from "@mui/material";
import { MonitorButton } from "../../components/MonitorControls";

// Platzhalter ohne Inhalt (api-Muster „HTTP 409“) sind keine Servermeldung.
const PLACEHOLDER = /^HTTP \d{3}$/;
// Technische Fehler (Ladezeit, Maßnahme 5a): Ihre Meldung ist Rohtext der Laufzeit („signal is aborted without reason“,
// „fetch failed“, HTTP-Status von GitHub); nur der Text des Codes wird gezeigt. STAGE_BOARD_TIMEOUT: Gesamtfrist des
// Servers je Board- oder Detailsaufbau (503, trace.js), seine Antwort nennt nur die hängende Phase.
const TECHNICAL = new Set(["STAGE_READ_TIMEOUT", "STAGE_ACTION_TIMEOUT", "STAGE_ACTION_OUTCOME_UNKNOWN", "STAGE_CONNECTION_FAILED", "STAGE_REQUEST_FAILED", "GITHUB_UNAVAILABLE", "STAGE_BOARD_TIMEOUT", "GITHUB_BUDGET_STORE_UNAVAILABLE"]);

/**
 * Fehler einer Stufenbranch-Antwort (Vertrag 14, 16.2): Überschrift aus `stageBranches.errors.<code>`, sonst `title`;
 * darunter die Servermeldung, sofern sie mehr sagt als die Überschrift. Codes ohne eigenen Text (etwa `REPO_RESET_ACTIVE`)
 * zeigen so den Servertext unter der Überschrift der Aktion.
 */
// targetName(stage, targetId) (optional): Instanzname eines Ziels für die Liste betroffener Ziele (Mehrziel, Integration I1).
// onRetry (optional): Lesefehler bekommen den Knopf „Erneut laden“ an der bestehenden Ladelogik des Aufrufers.
// Keine Antwort in der Frist ist kein Fehler der Aktion: Sie läuft meist auf dem Server weiter. Hinweis statt Fehler.
const STILL_RUNNING = new Set(["STAGE_ACTION_TIMEOUT", "STAGE_ACTION_OUTCOME_UNKNOWN"]);

export default function StageErrorAlert({ error, title, severity: requestedSeverity = "error", sx, targetName, onRetry, retryDisabled = false }) {
  const { t, i18n } = useTranslation("freigabe");
  if (!error) return null;
  const severity = STILL_RUNNING.has(error.code) ? "info" : requestedSeverity;
  const known = !!error.code && i18n.exists(`stageBranches.errors.${error.code}`, { ns: "freigabe" });
  // Platzhalter wie {{packageId}} und {{topicName}} kommen aus den details der Antwort (Vertrag 26.5), eine Regel mit
  // stageBranchesErrorText.
  const knownText = known ? stageBranchesErrorText(error, i18n.language) || t(`stageBranches.errors.${error.code}`) : title;
  // Schutz: Fehlen dem Text Werte für Platzhalter ({{seconds}}, {{packageId}} …), nie den Rohtext zeigen, sondern die Überschrift.
  const heading = /\{\{\w+\}\}/.test(String(knownText)) ? title : knownText;
  const message = String(error.message || "").trim();
  const body = message && message !== heading && !PLACEHOLDER.test(message) && !TECHNICAL.has(error.code) ? message : "";
  const detail = stageBranchesErrorDetail(error, { language: i18n.language, targetName });
  // Listencodes: Die Liste betroffener Stufen und Ziele steht in detail. Enthält die Servermeldung sie schon, entfällt das
  // Duplikat; bei bekanntem Code bleibt die Überschrift, der Rohtext der Antwort würde dieselbe Liste wiederholen.
  const listed = !!detail && (known || body.includes(detail) || detail.includes(body));
  const shownBody = listed ? "" : body;
  const action = typeof onRetry === "function"
    ? <MonitorButton size="small" variant="text" disabled={retryDisabled} onClick={() => onRetry()}>{t("stageBranches.details.retry")}</MonitorButton> : undefined;
  return <Alert severity={severity} sx={sx} action={action}>
    <AlertTitle sx={{ mb: shownBody || detail ? 0.5 : 0 }}>{heading}</AlertTitle>
    {shownBody}
    {detail && <div>{detail}</div>}
  </Alert>;
}
